import { Fragment, type FocusEvent, type ReactNode, type Ref } from 'react';
import { ErrorMessage } from './ErrorMessage';
import { Fieldset } from './Fieldset';
import { Hint } from './Hint';
import { RequiredMarker } from './RequiredMarker';
import { cx, describedBy } from './cx';

export interface ChoiceItem {
  value: string;
  label: ReactNode;
  lang?: string;
  hint?: ReactNode;
  /** Content revealed below the item while it is selected (GOV.UK conditional reveal; removed from the DOM otherwise). */
  conditional?: ReactNode;
}

export interface RadiosProps {
  name: string;
  /** Id of the first input (other inputs get `{idPrefix}-{n}`), used for error-summary links. */
  idPrefix: string;
  legend: ReactNode;
  legendClassName?: string;
  legendIsPageHeading?: boolean;
  legendLang?: string;
  hint?: ReactNode;
  hintLang?: string;
  error?: string;
  /** a red asterisk after the label / legend ("(povinné)" for screen readers) */
  required?: boolean;
  items: ChoiceItem[];
  value: string | null | undefined;
  onChange: (value: string) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  inline?: boolean;
  small?: boolean;
  /** Rendered as aria-disabled: still focusable and announced, but changes are blocked. */
  ariaDisabled?: boolean;
  extraDescribedBy?: string;
  beforeItems?: ReactNode;
  formGroupClassName?: string;
  firstInputRef?: Ref<HTMLInputElement>;
}

export function choiceItemId(idPrefix: string, index: number): string {
  return index === 0 ? idPrefix : `${idPrefix}-${index + 1}`;
}

/** govuk/components/radios */
export function Radios({
  name,
  idPrefix,
  legend,
  legendClassName,
  legendIsPageHeading,
  legendLang,
  hint,
  hintLang,
  error,
  required,
  items,
  value,
  onChange,
  onBlur,
  inline,
  small,
  ariaDisabled,
  extraDescribedBy,
  beforeItems,
  formGroupClassName,
  firstInputRef,
}: RadiosProps) {
  const hintId = hint ? `${idPrefix}-hint` : undefined;
  const errorId = error ? `${idPrefix}-error` : undefined;
  const isConditional = items.some((i) => i.conditional !== undefined);
  return (
    <div className={cx('govuk-form-group', error && 'govuk-form-group--error', formGroupClassName)}>
      <Fieldset
        legend={
          <>
            {legend}
            <RequiredMarker required={required} />
          </>
        }
        legendClassName={legendClassName}
        legendIsPageHeading={legendIsPageHeading}
        legendLang={legendLang}
        aria-describedby={describedBy(extraDescribedBy, hintId, errorId)}
      >
        {hint && (
          <Hint id={hintId} lang={hintLang}>
            {hint}
          </Hint>
        )}
        {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
        {beforeItems}
        <div
          className={cx(
            'govuk-radios',
            inline && 'govuk-radios--inline',
            small && 'govuk-radios--small',
            isConditional && 'govuk-radios--conditional',
          )}
          data-module={isConditional ? 'govuk-radios' : undefined}
        >
          {items.map((item, index) => {
            const id = choiceItemId(idPrefix, index);
            const checked = value === item.value;
            const itemHintId = item.hint ? `${id}-item-hint` : undefined;
            const conditionalId = `conditional-${id}`;
            const revealed = checked && item.conditional !== undefined && item.conditional !== null;
            return (
              <Fragment key={item.value}>
                <div className="govuk-radios__item">
                  <input
                    ref={index === 0 ? firstInputRef : undefined}
                    className="govuk-radios__input"
                    id={id}
                    name={name}
                    type="radio"
                    value={item.value}
                    checked={checked}
                    aria-disabled={ariaDisabled ? true : undefined}
                    aria-describedby={itemHintId}
                    onBlur={onBlur}
                    aria-controls={revealed ? conditionalId : undefined}
                    onChange={() => {
                      if (!ariaDisabled) onChange(item.value);
                    }}
                    onClick={(e) => {
                      if (ariaDisabled) e.preventDefault();
                    }}
                  />
                  <label className="govuk-label govuk-radios__label" htmlFor={id} lang={item.lang}>
                    {item.label}
                  </label>
                  {item.hint && (
                    <span id={itemHintId} className="govuk-hint govuk-radios__hint">
                      {item.hint}
                    </span>
                  )}
                </div>
                {revealed && (
                  <div className="govuk-radios__conditional" id={conditionalId}>
                    {item.conditional}
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </Fieldset>
    </div>
  );
}
